import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser, faCheckCircle, faCircleXmark, faUtensils, faRunning, faWater,
    faMoon, faSpinner, faExclamationTriangle, faChevronLeft, faChevronRight,
    faBook, faAppleAlt, faBed, faCheckSquare
} from '@fortawesome/free-solid-svg-icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import './AppProfileModal.css';
import './NutritionDailyView.css';
import './HabitTracker.css';
import './AppUserDetails.css';
import HeartFillComponent from './HeartFillComponent';
import NutritionDailyView from './NutritionDailyView';
import AppUserDetails from './AppUserDetails';
import Lottie from 'react-lottie-player';
import PatientLongevityAI from './PatientLongevityAI/PatientLongevityAI';

const formatShortDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : "N/A";
const formatTime = d => d ? new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "N/A";
const calculateDuration = (start, end) => {
    if (!start || !end) return { hours: 0, minutes: 0, text: 'N/A', totalHours: 0 };
    const diff = new Date(end) - new Date(start);
    if (isNaN(diff) || diff < 0) return { hours: 0, minutes: 0, text: 'Error', totalHours: 0 };
    const totalMinutes = diff / (1000 * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    return { hours, minutes, text: `${hours}h ${minutes}m`, totalHours: totalMinutes / 60 };
};
const getLatestNRecordsAsc = (dataArray = [], dateKey = 'date', n = 10) =>
    dataArray?.length
        ? [...dataArray].sort((a, b) => new Date(b[dateKey]) - new Date(a[dateKey])).slice(0, n).reverse()
        : [];


        

function CalendarSquareHeatmap({ habits = [], selectedDate, setSelectedDate }) {
    const [days, setDays] = useState([]);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    
    // Set today as default selected date when component mounts
    useEffect(() => {
        if (!selectedDate) {
            setSelectedDate(new Date());
        }
    }, [selectedDate]);
    
    // Generate days for the current month only
    useEffect(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        
        // First day of the month
        const firstDay = new Date(year, month, 1);
        
        // Last day of the month
        const lastDay = new Date(year, month + 1, 0);
        
        const arr = [];
        for (let d = 1; d <= lastDay.getDate(); d++) {
            const date = new Date(year, month, d);
            const iso = date.toISOString().split('T')[0];
            
            let completedCount = 0;
            habits.forEach(h => {
                if ((h.completions || []).some(c => c.startsWith(iso))) completedCount++;
            });
            
            arr.push({
                date: new Date(date),
                dateStr: iso,
                totalHabits: habits.length,
                completedCount
            });
        }
        
        setDays(arr);
    }, [habits, currentMonth]);
    
    const handleSelect = (d) => setSelectedDate(d.date);
    
    // Go to previous month
    const prevMonth = () => {
        const prev = new Date(currentMonth);
        prev.setMonth(prev.getMonth() - 1);
        setCurrentMonth(prev);
    };
    
    // Go to next month
    const nextMonth = () => {
        const next = new Date(currentMonth);
        next.setMonth(next.getMonth() + 1);
        setCurrentMonth(next);
    };
    
    // Format month name and year
    const formatMonthYear = (date) => {
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };
    
    return (
        <>
            <div className="APM-month-navigation">
                <button className="APM-month-nav-btn" onClick={prevMonth}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <h3 className="APM-month-title">{formatMonthYear(currentMonth)}</h3>
                <button className="APM-month-nav-btn" onClick={nextMonth}>
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
            <div className="APM-heatmap-grid">
                {days.map((dObj, idx) => {
                    const ratio = dObj.totalHabits ? dObj.completedCount / dObj.totalHabits : 0;
                    let backgroundColor;
                    
                    if (ratio === 0) {
                        backgroundColor = '#f7fafc'; // Very light blue/gray for empty days
                    } else if (ratio < 0.25) {
                        backgroundColor = '#e1eeff'; // Very light blue
                    } else if (ratio < 0.5) {
                        backgroundColor = '#bbd6f1'; // Light blue
                    } else if (ratio < 0.75) {
                        backgroundColor = '#6fa9dc'; // Medium blue
                    } else {
                        backgroundColor = '#20558a'; // Full blue matching our theme
                    }
                    const isSelected =
                        selectedDate &&
                        dObj.date.toISOString().split('T')[0] === selectedDate.toISOString().split('T')[0];
                    return (
                        <div
                            key={idx}
                            onClick={() => handleSelect(dObj)}
                            className={`APM-heatmap-day${isSelected ? ' selected' : ''}`}
                            style={{ backgroundColor }}
                            title={`Habits completed: ${dObj.completedCount}/${dObj.totalHabits}\n${formatShortDate(dObj.date)}`}
                        >
                            <span>{dObj.date.getDate()}</span>
                        </div>
                    );
                })}
            </div>
        </>
    );
}

function HabitList({ habits = [], date }) {
    if (!date) return <div className="habit-select-prompt">Select a date above</div>;
    
    const dateStr = date.toISOString().split('T')[0];
    const formattedDate = date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    
    // Group habits by status
    const completedHabits = [];
    const missedHabits = [];
    
    habits.forEach(habit => {
        const completed = (habit.completions || []).some(c => c.startsWith(dateStr));
        if (completed) {
            completedHabits.push(habit);
        } else {
            missedHabits.push(habit);
        }
    });
    
    // Get habit category icon using FontAwesome icons
    const getHabitIcon = (description = '') => {
        const lowerDesc = description.toLowerCase();
        if (lowerDesc.includes('exercise') || lowerDesc.includes('workout') || lowerDesc.includes('walk')) {
            return faRunning;
        } else if (lowerDesc.includes('water') || lowerDesc.includes('drink')) {
            return faWater;
        }  else if (lowerDesc.includes('read') || lowerDesc.includes('book')) {
            return faBook;
        } else if (lowerDesc.includes('eat') || lowerDesc.includes('meal') || lowerDesc.includes('food')) {
            return faAppleAlt;
        } else if (lowerDesc.includes('sleep') || lowerDesc.includes('bed')) {
            return faBed;
        }
        return faCheckSquare;
    };
    
    const calcCompletion = () => {
        if (!habits.length) return { percent: 0, fraction: '0/0' };
        const percent = Math.round((completedHabits.length / habits.length) * 100);
        const fraction = `${completedHabits.length}/${habits.length}`;
        return { percent, fraction };
    };
    
    const completion = calcCompletion();
    
    return (
        <div className="habit-tracker-container">
            <div className="habit-tracker-header">
                <div className="habit-date">{formattedDate}</div>
                {habits.length > 0 && (
                    <div className="habit-progress">
                        <div className="habit-progress-bar">
                            <div 
                                className="habit-progress-fill" 
                                style={{ width: `${completion.percent}%` }}
                            />
                        </div>
                        <div className="habit-progress-text">{completion.fraction}</div>
                    </div>
                )}
            </div>
            
            <div className="habit-list-container">
                {!habits.length && <div className="habit-empty">No habits tracked for this user</div>}
                
                {completedHabits.length > 0 && (
                    <div className="habit-section">
                        <div className="habit-section-header">
                            <FontAwesomeIcon icon={faCheckCircle} className="habit-section-icon completed" />
                            <span>Completed</span>
                        </div>
                        <div className="habit-items">
                            {completedHabits.map(habit => (
                                <div key={habit.id} className="habit-item completed">
                                    <div className="habit-item-content">
                                        <div className="habit-icon">
                                            <FontAwesomeIcon icon={getHabitIcon(habit.description)} color="#4a5568" />
                                        </div>
                                        <div className="habit-details">
                                            <div className="habit-description">{habit.description}</div>
                                            {habit.goalTime && (
                                                <div className="habit-time">Goal time: {habit.goalTime?.slice(0,5)}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="habit-status">
                                        <div className="habit-status-indicator completed">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {missedHabits.length > 0 && (
                    <div className="habit-section">
                        <div className="habit-section-header">
                            <FontAwesomeIcon icon={faCircleXmark} className="habit-section-icon missed" />
                            <span>Missed</span>
                        </div>
                        <div className="habit-items">
                            {missedHabits.map(habit => (
                                <div key={habit.id} className="habit-item missed">
                                    <div className="habit-item-content">
                                        <div className="habit-icon">
                                            <FontAwesomeIcon icon={getHabitIcon(habit.description)} color="#9c9c9c" />
                                        </div>
                                        <div className="habit-details">
                                            <div className="habit-description">{habit.description}</div>
                                            {habit.goalTime && (
                                                <div className="habit-time">Goal time: {habit.goalTime?.slice(0,5)}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="habit-status">
                                        <div className="habit-status-indicator missed">
                                            <FontAwesomeIcon icon={faCircleXmark} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function HRDailyBarChart({ data = [], selectedDay, onBarClick }) {
    if (!data.length) return <div className="APM-no-data"></div>;
    const chartData = data.slice(-10);
    const lows = chartData.map(d => d.minBpm ?? 40);
    const highs = chartData.map(d => d.maxBpm ?? 120);
    const dataLow = Math.min(...lows, 40);
    const dataHigh = Math.max(...highs, dataLow + 40);
    const scaleRange = dataHigh - dataLow || 1;
    const yTicks = [
        { value: dataHigh, percent: 100 },
        { value: Math.round((dataHigh + dataLow) / 2), percent: 50 },
        { value: dataLow, percent: 0 }
    ];
    const avgHR = chartData.length
        ? (chartData.reduce((a, b) => a + ((b.minBpm + b.maxBpm) / 2), 0) / chartData.length).toFixed(0)
        : "N/A";

    return (
        <div className="APM-chart-container">
            <div className="APM-chart-header">
                <h3>Heart Rate</h3>
                <div className="APM-chart-avg">{avgHR} <div className="APM-chart-unit">bpm</div></div>
            </div>
            <div className="APM-bar-wrapper">
                <div className="APM-y-axis-area">
                    {yTicks.map((tick, i) => (
                        <div key={i} className="APM-y-tick" style={{ bottom: `${tick.percent}%` }}>
                            <span className="APM-y-label">{tick.value}</span>
                            <span className="APM-y-grid" />
                        </div>
                    ))}
                </div>
                <div className="APM-bar-area">
                    {chartData.map((item, i) => {
                        const min = item.minBpm ?? dataLow;
                        const max = item.maxBpm ?? min;
                        const barHeight = ((max - min) / scaleRange) * 100;
                        const barBottom = ((min - dataLow) / scaleRange) * 100;
                        const sel = selectedDay && item.date === selectedDay;
                        return (
                            <div key={i} className="APM-bar-barcol" style={{ position: 'relative' }}>
                                <div
                                    className={`APM-bar-bar${sel ? " sel" : ""}`}
                                    style={{
                                        position: 'absolute',
                                        left: '25%',
                                        width: '50%',
                                        bottom: `${barBottom}%`,
                                        height: `${barHeight}%`,
                                        backgroundColor: "#90caf9",
                                        borderColor: "#68a4e1"
                                    }}
                                    title={`Min: ${min}, Max: ${max}\n${item.date}`}
                                    onClick={() => onBarClick(item.date)}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="APM-bar-x-labels-row">
                {chartData.map((item, i) => (
                    <div key={i} className="APM-bar-x-label">
                        {/* Only show every other label when more than 4 records */}
                        {chartData.length <= 4 || i % 2 === 0 ? formatShortDate(item.date) : ''}
                    </div>
                ))}
            </div>
        </div>
    );
}

function HRHourlyBarChart({ heartRate = [], dayISO }) {
    if (!heartRate.length || !dayISO) return <div className="APM-no-data"></div>;
    const hours = {};
    heartRate.forEach(hr => {
        const ts = new Date(hr.timestamp);
        const ds = ts.toISOString().split('T')[0];
        if (ds !== dayISO) return;
        const h = ts.getHours();
        if (!hours[h]) hours[h] = { min: hr.bpm, max: hr.bpm };
        else {
            hours[h].min = Math.min(hours[h].min, hr.bpm);
            hours[h].max = Math.max(hours[h].max, hr.bpm);
        }
    });
    const hourList = Object.keys(hours).map(h => ({ hour: Number(h), ...hours[h] })).sort((a, b) => a.hour - b.hour);
    if (!hourList.length) return <div className="APM-no-data"></div>;

    const minBpm = Math.min(...hourList.map(h => h.min));
    const maxBpm = Math.max(...hourList.map(h => h.max), minBpm + 20);
    const range = maxBpm - minBpm || 1;
    const avgDayHR = hourList.length
        ? (hourList.reduce((a, b) => a + ((b.min + b.max) / 2), 0) / hourList.length).toFixed(0)
        : "N/A";
    const yTicks = [
        { value: maxBpm, percent: 100 },
        { value: Math.round((maxBpm + minBpm) / 2), percent: 50 },
        { value: minBpm, percent: 0 }
    ];

    return (
        <div className="APM-chart-container">
            <div className="APM-chart-header">
                <h3>Hourly Heart Rate</h3>
                <div className="APM-chart-avg">{avgDayHR} <div className="APM-chart-unit">bpm</div></div>
            </div>
            <div className="APM-bar-wrapper">
                <div className="APM-y-axis-area">
                    {yTicks.map((tick, i) => (
                        <div key={i} className="APM-y-tick" style={{ bottom: `${tick.percent}%` }}>
                            <span className="APM-y-label">{tick.value}</span>
                            <span className="APM-y-grid" />
                        </div>
                    ))}
                </div>
                <div className="APM-bar-area">
                    {hourList.map(hrhr => {
                        const { hour, min, max } = hrhr;
                        const barHeight = ((max - min) / range) * 100;
                        const barBottom = ((min - minBpm) / range) * 100;
                        return (
                            <div key={hour} className="APM-bar-barcol" style={{ position: 'relative' }}>
                                <div
                                    className="APM-bar-bar"
                                    style={{
                                        position: 'absolute',
                                        left: '25%',
                                        width: '50%',
                                        bottom: `${barBottom}%`,
                                        height: `${barHeight}%`,
                                        backgroundColor: "#90caf9",
                                        borderColor: "#68a4e1"
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="APM-bar-x-labels-row">
                {hourList.map((hrhr, i) => (
                    <div key={hrhr.hour} className="APM-bar-x-label">
                        {/* Only show every other label when more than 4 records */}
                        {hourList.length <= 4 || i % 2 === 0 ? String(hrhr.hour).padStart(2,'0') : ''}
                    </div>
                ))}
            </div>
        </div>
    );
}

function StepsLineChart({ data = [] }) {
    if (!data.length) return <div className="APM-no-data"></div>;
    
    const chartData = data.map(item => ({
        date: formatShortDate(item.date),
        steps: item.steps,
        fullDate: item.date
    }));
    
    const maxSteps = Math.max(...data.map(d => d.steps), 1);
    const avgSteps = data.length ? (data.reduce((a, b) => a + b.steps, 0) / data.length).toFixed(0) : "N/A";
    
    const gradientOffset = () => {
        const dataMax = Math.max(...data.map(d => d.steps));
        const dataMin = Math.min(...data.map(d => d.steps));
        
        if (dataMax <= 0) return 0;
        if (dataMin >= 0) return 1;
        
        return dataMin / (dataMin - dataMax);
    };
    
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="recharts-custom-tooltip">
                    <p className="tooltip-date">{label}</p>
                    <p className="tooltip-steps">{`${payload[0].value.toLocaleString()} steps`}</p>
                </div>
            );
        }
        return null;
    };
    
    return (
        <div className="APM-chart-container">
            <div className="APM-chart-header">
                <h3>Daily Steps</h3>
                <div className="APM-chart-avg">{avgSteps} <span className="APM-chart-unit">steps</span></div>
            </div>
            <div className="APM-chart-content">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={chartData}
                        margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                        <defs>
                            <linearGradient id="stepsGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#90caf9" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#90caf9" stopOpacity={0.1} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis 
                            dataKey="date" 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#718096' }}
                        />
                        <YAxis 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#718096' }}
                            tickFormatter={(value) => value.toLocaleString()}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                            type="monotone" 
                            dataKey="steps" 
                            stroke="#90caf9" 
                            strokeWidth={2}
                            fill="url(#stepsGradient)" 
                            activeDot={{ r: 6, strokeWidth: 0, fill: '#174573' }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function ValueBarChart({ data = [], valueKey, unit, title }) {
    if (!data.length) return <div className="APM-no-data"></div>;
    const maxValue = Math.max(...data.map(d => d[valueKey]), 1);
    const avgValue = data.length ? (data.reduce((a, b) => a + b[valueKey], 0) / data.length).toFixed(1) : "N/A";
    
    // Generate 5-7 nice rounded tick values for better readability
    const step = maxValue / 4;
    const yTicks = Array.from({ length: 5 }, (_, i) => ({ 
        value: Math.round(i * step), 
        percent: (i * step / maxValue) * 100 
    }));
    
    // Custom tooltip for water intake that looks like the steps tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="recharts-custom-tooltip">
                    <p className="tooltip-date">{label}</p>
                    <p className="tooltip-steps">{`${payload[0].value.toLocaleString()} ${unit || ''}`}</p>
                </div>
            );
        }
        return null;
    };
    
    return (
        <div className="APM-chart-container">
            <div className="APM-chart-header">
                <h3>{title}</h3>
                <div className="APM-chart-avg">{avgValue} <span className="APM-chart-unit">{unit}</span></div>
            </div>
            <div className="APM-chart-content">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={data.map(item => ({
                            date: formatShortDate(item.date),
                            value: item[valueKey],
                            fullDate: item.date
                        }))}
                        margin={{ top: 10, right: 10, left: -35, bottom: -10 }}
                    >
                        <defs>
                            <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#90caf9" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#90caf9" stopOpacity={0.1} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis 
                            dataKey="date" 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#718096' }}
                        />
                        <YAxis 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#718096' }}
                            tickFormatter={(value) => value.toLocaleString()}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                            type="monotone" 
                            dataKey="value" 
                            stroke="#90caf9" 
                            strokeWidth={2}
                            fill="url(#valueGradient)" 
                            activeDot={{ r: 6, strokeWidth: 0, fill: '#174573' }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function SleepBarChart({ sleep = [] }) {
    const last7 = getLatestNRecordsAsc(sleep, 'date', 7);
    
    // Convert sleep data to a format suitable for AreaChart
    const chartData = last7.map(s => {
        const duration = calculateDuration(s.sleepTime, s.wakeTime);
        return {
            date: formatShortDate(s.date),
            value: duration.totalHours,
            fullDate: s.date,
            sleepTime: s.sleepTime,
            wakeTime: s.wakeTime,
            durationText: duration.text
        };
    });
    
    // Early return for no data - EXACT match to ValueBarChart approach
    if (!chartData.length) return <div className="APM-no-data"></div>;
    
    const maxValue = Math.max(...chartData.map(d => d.value), 8);
    const avgDuration = (chartData.reduce((a, b) => a + b.value, 0) / chartData.length).toFixed(1);
    
    // Custom tooltip for sleep data
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="recharts-custom-tooltip">
                    <p className="tooltip-date">{label}</p>
                    <p className="tooltip-steps">{`${data.value.toFixed(1)} hours`}</p>
                    <p className="tooltip-time">{`${formatTime(data.sleepTime)} → ${formatTime(data.wakeTime)}`}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="APM-chart-container">
            <div className="APM-chart-header">
                <h3>Sleep Duration</h3>
                <div className="APM-chart-avg">{avgDuration} <div className="APM-chart-unit">h</div></div>
            </div>
            <div className="APM-chart-content">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={chartData}
                        margin={{ top: 10, right: 10, left: -35, bottom: -10 }}
                    >
                        <defs>
                            <linearGradient id="sleepGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#90caf9" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#90caf9" stopOpacity={0.1} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis 
                            dataKey="date" 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#718096' }}
                        />
                        <YAxis 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#718096' }}
                            tickFormatter={(value) => `${value}h`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                            type="monotone" 
                            dataKey="value" 
                            stroke="#90caf9" 
                            strokeWidth={2}
                            fill="url(#sleepGradient)" 
                            activeDot={{ r: 6, strokeWidth: 0, fill: '#174573' }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}


function AppProfileModal({ appUser }) {
    const [healthData, setHealthData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [calendarDate, setCalendarDate] = useState(null);
    const [chartDay, setChartDay] = useState(null);
    const [aiPanelOpen, setAiPanelOpen] = useState(false);
    const stepsForHeartComponent = React.useMemo(() => {
        console.log("Recalculating stepsForHeart (last 30 asc)");
        // getLatestNRecordsAsc expects data sorted DESC first, then reverses.
        // Let's ensure it gets raw data and sorts correctly.
         const rawSteps = healthData?.steps || [];
         return getLatestNRecordsAsc(rawSteps, 'date', 30); // Get last 30 days ASC
    }, [healthData?.steps]);
    useEffect(() => {
        if (appUser && appUser.User_ID) fetchHealthData(appUser.User_ID);
        else {
            setHealthData(null);
            setError(null);
            setLoading(false);
        }
    }, [appUser]);

    const fetchHealthData = async (userId) => {
        setLoading(true);
        setError(null);
        setHealthData(null);
        try {
            const response = await fetch(`https://service9.szapfs.org/patient/${userId}/health-data`);
            if (!response.ok) throw new Error(`Network error (${response.status})`);
            const data = await response.json();
            if (!data || typeof data !== 'object') throw new Error('Invalid data format');
            setHealthData(data);
            console.log(data)
        } catch (error) {
            setError(`Load failed: ${error.message}`);
            setHealthData(null);
        } finally {
            setLoading(false);
        }
    };
    const [lottieData, setLottieData] = useState(null);


    // Load Lottie animation on component mount
    useEffect(() => {
        fetch('https://lottie.host/e225fcd9-4a02-4001-a8f5-b67e21e1bc42/nAt6uKG3C5.json')
            .then(response => response.json())
            .then(data => setLottieData(data))
            .catch(error => console.error('Error loading Lottie animation:', error));
    }, []);

    const dailyHRList = React.useMemo(() => {
        if (!healthData?.heartRate?.length) return [];
        const daily = {};
        healthData.heartRate.forEach(hr => {
            const d = new Date(hr.timestamp).toISOString().split('T')[0];
            if (!daily[d]) daily[d] = { date: d, minBpm: hr.bpm, maxBpm: hr.bpm };
            else {
                daily[d].minBpm = Math.min(daily[d].minBpm, hr.bpm);
                daily[d].maxBpm = Math.max(daily[d].maxBpm, hr.bpm);
            }
        });
        return Object.values(daily).sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-10);
    }, [healthData]);

    const stepsLast10 = getLatestNRecordsAsc(healthData?.steps || [], 'date', 10);
    const waterLast10 = getLatestNRecordsAsc(healthData?.waterIntake || [], 'date', 10);

    useEffect(() => {
        if (dailyHRList.length && !chartDay) setChartDay(dailyHRList[dailyHRList.length - 1]?.date);
    }, [dailyHRList, chartDay]);

    if (!appUser) return <div className="APM-no-user">Select app user</div>;
    if (error) return <div className="APM-error"><FontAwesomeIcon icon={faExclamationTriangle} /> {error}</div>;

    return (
        <div className="APM-container-final">
            <PatientLongevityAI 
                isOpen={aiPanelOpen} 
                onClose={() => setAiPanelOpen(false)} 
                userData={appUser}
            />
            <div className="APM-container-final-left">
            <div className="APM-container-final-left-bottom-highest">
                <button className="ai-analysis-button" onClick={() => setAiPanelOpen(true)}>
                    <Lottie
                        loop
                        animationData={lottieData}
                        play
                        className="APM-container-final-left-bottom-highest-lottie"
                    />
                    <span className="ai-analysis-text">Longevity AI</span>
                    <FontAwesomeIcon icon={faChevronRight} color="grey" size="xs" />
                </button>
            </div>
                <div className="APM-container-final-left-top">
                    <CalendarSquareHeatmap
                        habits={healthData?.habits || []}
                        selectedDate={calendarDate}
                        setSelectedDate={setCalendarDate}
                    />
                </div>
                <div className="APM-container-final-left-bottom">
                    <HabitList habits={healthData?.habits || []} date={calendarDate} />
                </div>
             
            </div>
            <div className="APM-container-final-right">
            <div className="APM-container-final-right-wrap">
                <AppUserDetails userDetails={healthData} />
                <div className="APM-container-final-right-top">
                    <ValueBarChart
                        data={waterLast10}
                        valueKey="amountLiters"
                        unit="L"
                        title="Water Intake"
                    />
                                        <SleepBarChart sleep={healthData?.sleep || []} />

                        <HRDailyBarChart
                            data={dailyHRList}
                            selectedDay={chartDay}
                            onBarClick={(daystr) => setChartDay(daystr)}
                        />
                        <HRHourlyBarChart
                            heartRate={healthData?.heartRate || []}
                            dayISO={chartDay}
                        />

                </div>
                <div className="APM-container-final-right-middle">
                    <div className="APM-container-final-right-middle-left">
                        {/* Only apply the chart container if there's step data */}
                        {stepsForHeartComponent && stepsForHeartComponent.length > 0 ? (
                            <div className="APM-chart-container">
                                <HeartFillComponent stepsData={stepsForHeartComponent} />
                            </div>
                        ) : (
                            <HeartFillComponent stepsData={stepsForHeartComponent} />
                        )}
                    </div>
                    <div className="APM-container-final-right-middle-middle">
                        <StepsLineChart data={stepsLast10} />
                    </div>
                </div>
                <div className="APM-container-final-right-bottom">
                    <NutritionDailyView healthData={healthData} />
                </div>
            </div>
            </div>
        </div>
    );
}

export default AppProfileModal;
